import React, { useEffect, useState } from 'react';
import InvoiceLink from './InvoiceLink';
import { PartyPopper, MapPin, Star, Wallet, Users, Smartphone } from 'lucide-react';
import apiService from '../apiService';
import { mmPay } from './mmPay';

/**
 * Salles & espaces événementiels — web : liste (recherche par date / formule / invités),
 * fiche + réservation immédiate avec acompte (solde Mbiyo ou Mobile Money pay-first),
 * mes réservations (solde via Mbiyo, annulation). Mêmes endpoints que l'app native.
 */
const TYPE_LABEL = { party_hall: '🎉 Salle de fête', show_hall: '🎭 Salle de spectacle', conference: '🎤 Salle de conférence', outdoor: '🌳 Espace extérieur', tent: '⛺ Chapiteau', rooftop: '🏙️ Rooftop', other: '🏛️ Autre' };
const FORMULA_LABEL = { full_day: 'Journée', morning: 'Matin', afternoon: 'Après-midi', evening: 'Soirée', night: 'Nuit', weekend: 'Week-end', hourly: 'À l\'heure' };
const EVENT_LABEL = { wedding: 'Mariage', birthday: 'Anniversaire', conference: 'Conférence', concert: 'Concert', worship: 'Culte', funeral: 'Deuil', corporate: 'Entreprise', other: 'Autre' };
const AMENITY_LABEL = { stage: 'Scène', sound_system: 'Sono', projector: 'Projecteur / écran', air_conditioning: 'Climatisation', generator: 'Groupe électrogène', parking: 'Parking', kitchen: 'Cuisine', toilets: 'Toilettes', dressing_room: 'Loge', wifi: 'Wi-Fi', wheelchair: 'Accès PMR', security: 'Sécurité', lighting: 'Éclairage', tables_chairs: 'Tables & chaises', bar: 'Bar', garden: 'Jardin' };
const FC = (n) => `${Math.round(Number(n) || 0).toLocaleString('fr-FR')} FC`;
const d2s = (d) => d.toISOString().split('T')[0];

export function VenuesList({ nav, citySlug, supplierId }) {
  const [list, setList] = useState(null);
  const [date, setDate] = useState('');
  const [slot, setSlot] = useState('full_day');
  const [guests, setGuests] = useState('');
  const [type, setType] = useState('');

  useEffect(() => {
    const qs = new URLSearchParams();
    if (citySlug) qs.set('city_slug', citySlug);
    if (date) { qs.set('date', date); qs.set('slot', slot); }
    if (guests) qs.set('guests', guests);
    if (type) qs.set('venue_type', type);
    setList(null);
    apiService.get(`/venues?${qs}`).then(r => setList((r?.venues || []).filter(v => !supplierId || v.supplier_id === supplierId))).catch(() => setList([]));
  }, [citySlug, supplierId, date, slot, guests, type]);

  return (
    <div className="mk-container">
      <div className="mk-panel" style={{ marginTop: 14 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10 }}>
          <div className="mk-field"><label>Date</label><input type="date" value={date} min={d2s(new Date())} onChange={e => setDate(e.target.value)} /></div>
          <div className="mk-field"><label>Formule</label>
            <select value={slot} onChange={e => setSlot(e.target.value)}>{Object.entries(FORMULA_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></div>
          <div className="mk-field"><label>Invités</label><input type="number" min="1" value={guests} onChange={e => setGuests(e.target.value)} placeholder="ex. 150" /></div>
          <div className="mk-field"><label>Type</label>
            <select value={type} onChange={e => setType(e.target.value)}><option value="">Tous</option>{Object.entries(TYPE_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
          <p className="mk-note" style={{ margin: 0 }}>{date ? `Salles libres le ${date} (${FORMULA_LABEL[slot]})` : 'Choisissez une date pour ne voir que les salles libres.'}</p>
          <button className="mk-btn ghost" onClick={() => nav({ page: 'venue-bookings' })}><PartyPopper size={16} /> Mes réservations</button>
        </div>
      </div>
      {list === null ? <div className="mk-center"><div className="mk-spin" /> Chargement…</div>
        : !list.length ? <div className="mk-center"><PartyPopper size={42} /> {date ? 'Aucune salle libre à cette date.' : 'Aucune salle disponible pour le moment.'}</div>
        : (
          <div className="mk-prodgrid" style={{ marginTop: 14 }}>
            {list.map(v => (
              <button className="mk-card" key={v.venue_id} onClick={() => nav({ page: 'venue', venueId: v.venue_id, date, slot })}>
                <div className="mk-card-imgwrap">{v.cover_image ? <img className="mk-card-img" src={v.cover_image} alt="" /> : <div className="mk-card-img mk-card-ph">🎉</div>}</div>
                <div className="mk-card-title">{v.name}</div>
                <div className="mk-meta">{TYPE_LABEL[v.venue_type] || v.venue_type} · <Users size={12} /> {v.capacity_seated || 0}/{v.capacity_standing || 0}{v.rating_count ? <> · <Star size={12} fill="#FFB900" color="#FFB900" /> {Number(v.rating).toFixed(1)}</> : null}</div>
                <div className="mk-card-price">{v.price_for_request != null ? FC(v.price_for_request) : v.min_price ? `dès ${FC(v.min_price)}` : 'Sur demande'}</div>
              </button>
            ))}
          </div>
        )}
    </div>
  );
}

export function VenueDetail({ venueId, nav, currentUser, isAuthenticated, initialDate, initialSlot }) {
  const [venue, setVenue] = useState(null);
  const [supplier, setSupplier] = useState(null);
  const [date, setDate] = useState(initialDate || '');
  const [slot, setSlot] = useState(initialSlot || 'full_day');
  const [startTime, setStartTime] = useState('14:00');
  const [hours, setHours] = useState('');
  const [eventType, setEventType] = useState('other');
  const [guests, setGuests] = useState('');
  const [optQty, setOptQty] = useState({});
  const [message, setMessage] = useState('');
  const [quote, setQuote] = useState(null);
  const [pay, setPay] = useState('balance');
  const [phone, setPhone] = useState('');
  const [phase, setPhase] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    apiService.get(`/venues/${venueId}`).then(r => {
      if (r?.venue) {
        setVenue(r.venue); setSupplier(r.supplier || null);
        if (r.venue.hourly?.enabled) { setHours(String(r.venue.hourly.min_hours || 2)); setStartTime(r.venue.hourly.open_time || '14:00'); }
        const keys = (r.venue.formulas || []).map(f => f.key);
        if (!initialSlot && !keys.includes('full_day')) setSlot(keys[0] || 'hourly');
      }
    }).catch(() => {});
  }, [venueId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!venue || !date) { setQuote(null); return; }
    const options = Object.entries(optQty).filter(([, q]) => q > 0).map(([k, q]) => `${k}:${q}`).join(',');
    const qs = new URLSearchParams({ date, slot, ...(slot === 'hourly' ? { start_time: startTime, hours } : {}), guests: guests || '1', options });
    apiService.get(`/venues/${venueId}/quote?${qs}`).then(r => setQuote(r?.success ? r : { error: r?.error })).catch(e => setQuote({ error: e?.error || e?.message }));
  }, [venue, venueId, date, slot, startTime, hours, guests, optQty]);

  if (!venue) return <div className="mk-center"><div className="mk-spin" /> Chargement…</div>;
  const pb = quote?.price_breakdown;
  const photos = [...(venue.cover_image ? [venue.cover_image] : []), ...(venue.images || []).map(i => i.url)];

  const book = async () => {
    if (!isAuthenticated) { setError('Connectez-vous pour réserver.'); return; }
    if (!quote?.available) return;
    setError(null); setBusy(true);
    try {
      let serdipay_payment_id = null;
      if (pay === 'mobile_money') {
        const r = await mmPay({ userId: currentUser?.user_id, amount: pb.deposit_amount, phone, label: `Acompte ${venue.name}`, planType: 'venue', onPhase: setPhase });
        serdipay_payment_id = r.paymentId;
      }
      const res = await apiService.post('/venue-bookings', {
        venue_id: venue.venue_id, date, slot, ...(slot === 'hourly' ? { start_time: startTime, hours: Number(hours) } : {}),
        guests_count: Number(guests) || 1, event_type: eventType,
        options: Object.entries(optQty).filter(([, q]) => q > 0).map(([option_id, qty]) => ({ option_id, qty })),
        client_message: message.trim(), payment_method: pay, ...(serdipay_payment_id ? { serdipay_payment_id } : {}),
      });
      if (!res?.success) throw new Error(res?.error || 'Réservation impossible');
      nav({ page: 'venue-bookings' });
    } catch (e) { setError(e?.error || e?.message || 'Erreur'); }
    finally { setBusy(false); setPhase(''); }
  };

  return (
    <div className="mk-container">
      <div className="mk-two">
        <div className="mk-panel" style={{ padding: 0, overflow: 'hidden' }}>
          {photos[0] && <img src={photos[0]} alt="" style={{ width: '100%', maxHeight: 340, objectFit: 'cover', display: 'block' }} />}
          {photos.length > 1 && <div style={{ display: 'flex', gap: 6, padding: 8, overflowX: 'auto' }}>{photos.slice(1).map((p, i) => <img key={i} src={p} alt="" style={{ width: 96, height: 70, objectFit: 'cover', borderRadius: 8, flexShrink: 0 }} />)}</div>}
          <div style={{ padding: 18 }}>
            <p className="mk-note">{TYPE_LABEL[venue.venue_type] || venue.venue_type}</p>
            <h3 style={{ fontSize: '1.3rem' }}>{venue.name}</h3>
            <p className="mk-note"><Users size={13} /> {venue.capacity_seated || 0} assis · {venue.capacity_standing || 0} debout{venue.surface_m2 ? ` · ${venue.surface_m2} m²` : ''}{venue.rating_count ? <> · <Star size={13} fill="#FFB900" color="#FFB900" /> {Number(venue.rating).toFixed(1)} ({venue.rating_count})</> : null}</p>
            {venue.location?.address && <p className="mk-note"><MapPin size={13} /> {venue.location.address}</p>}
            {venue.description && <p className="mk-desc" style={{ marginTop: 10 }}>{venue.description}</p>}
            <h4 style={{ margin: '14px 0 6px' }}>Formules</h4>
            {(venue.formulas || []).map(f => <div className="mk-line" key={f.key}><div className="info"><div className="name">{FORMULA_LABEL[f.key] || f.label}</div><div className="opt">{f.start_time} → {f.end_time}</div></div><b>{FC(f.price_weekday)}{f.price_weekend && f.price_weekend !== f.price_weekday ? ` · WE ${FC(f.price_weekend)}` : ''}</b></div>)}
            {venue.hourly?.enabled && <div className="mk-line"><div className="info"><div className="name">À l'heure</div><div className="opt">{venue.hourly.open_time} → {venue.hourly.close_time} · min. {venue.hourly.min_hours} h</div></div><b>{FC(venue.hourly.price_per_hour)} / h</b></div>}
            {venue.options?.length > 0 && <><h4 style={{ margin: '14px 0 6px' }}>Options</h4>{venue.options.map(o => <div className="mk-line" key={o.option_id}><div className="info"><div className="name">{o.label}</div></div><b>{FC(o.price)}{o.pricing === 'per_guest' ? ' / invité' : o.pricing === 'per_hour' ? ' / h' : ''}</b></div>)}</>}
            {venue.amenities?.length > 0 && <p className="mk-note" style={{ marginTop: 10 }}>Équipements : {venue.amenities.map(a => AMENITY_LABEL[a] || a).join(' · ')}</p>}
            <p className="mk-note">Règles : heure limite {venue.rules?.curfew_time || '—'} · alcool {venue.rules?.alcohol_allowed ? 'oui' : 'non'} · traiteur {{ free: 'libre', imposed: 'imposé', forbidden: 'interdit' }[venue.rules?.external_catering] || 'libre'}{venue.rules?.notes ? ` · ${venue.rules.notes}` : ''}</p>
            <div className="mk-pharmabox" style={{ marginTop: 12 }}>
              <p style={{ margin: 0 }}>💳 Acompte <b>{venue.deposit_percent} %</b> payé à la réservation via Mbiyo · 💵 solde {venue.all_payments_via_mbiyo ? 'à payer dans Mbiyo' : 'à régler au propriétaire'}{venue.security_deposit?.required ? <> · 🔒 caution <b>{FC(venue.security_deposit.amount)}</b> remise au propriétaire</> : null}</p>
              <ul style={{ margin: '6px 0 0', paddingLeft: 18, fontSize: '.85rem' }}>{(venue.cancellation_policy || []).map((l, i) => <li key={i}>{l}</li>)}</ul>
            </div>
            {supplier && <p className="mk-note" style={{ marginTop: 10 }}>Propriétaire : <b>{supplier.business_name}</b></p>}
          </div>
        </div>

        <div className="mk-panel">
          <h3>Réserver</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div className="mk-field"><label>Date</label><input type="date" value={date} min={d2s(new Date())} onChange={e => setDate(e.target.value)} /></div>
            <div className="mk-field"><label>Formule</label>
              <select value={slot} onChange={e => setSlot(e.target.value)}>
                {(venue.formulas || []).map(f => <option key={f.key} value={f.key}>{FORMULA_LABEL[f.key]} · {f.start_time}→{f.end_time}</option>)}
                {venue.hourly?.enabled && <option value="hourly">À l'heure</option>}
              </select></div>
            {slot === 'hourly' && <><div className="mk-field"><label>Début</label><input type="time" value={startTime} onChange={e => setStartTime(e.target.value)} /></div><div className="mk-field"><label>Durée (h)</label><input type="number" min={venue.hourly?.min_hours || 1} step="0.5" value={hours} onChange={e => setHours(e.target.value)} /></div></>}
            <div className="mk-field"><label>Type d'événement</label><select value={eventType} onChange={e => setEventType(e.target.value)}>{Object.entries(EVENT_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></div>
            <div className="mk-field"><label>Invités</label><input type="number" min="1" value={guests} onChange={e => setGuests(e.target.value)} /></div>
          </div>
          {venue.options?.length > 0 && (
            <div style={{ marginTop: 8 }}>
              {venue.options.map(o => { const q = optQty[o.option_id] || 0; return (
                <div className="mk-line" key={o.option_id}><div className="info"><div className="name">{o.label}</div><div className="opt">{FC(o.price)}{o.pricing === 'per_guest' ? ' / invité' : o.pricing === 'per_hour' ? ' / h' : ''}</div></div>
                  <div className="mk-qty"><button onClick={() => setOptQty(p => ({ ...p, [o.option_id]: Math.max(0, q - 1) }))}>−</button><span>{q}</span><button onClick={() => setOptQty(p => ({ ...p, [o.option_id]: Math.min(o.max_qty || 1, q + 1) }))}>+</button></div></div>); })}
            </div>
          )}
          <div className="mk-field" style={{ marginTop: 10 }}><label>Message au propriétaire (facultatif)</label><input value={message} onChange={e => setMessage(e.target.value)} placeholder="Précisions sur votre événement" /></div>

          {!date ? <p className="mk-note" style={{ marginTop: 10 }}>Choisissez une date.</p>
            : quote?.error ? <p className="mk-note err" style={{ marginTop: 10 }}>{quote.error}</p>
            : quote && !quote.available ? <p className="mk-note err" style={{ marginTop: 10 }}>⛔ Indisponible — {quote.unavailable_reason}</p>
            : pb ? (
              <>
                <div className="mk-total"><span>{quote.slot?.label}</span><span>{FC(pb.base_price)}</span></div>
                {(quote.options || []).map(o => <div className="mk-total" key={o.option_id}><span>{o.label} × {o.qty}</span><span>{FC(o.total)}</span></div>)}
                <div className="mk-total"><span><b>Total</b></span><span><b>{FC(pb.total_amount)}</b></span></div>
                <div className="mk-total" style={{ color: 'var(--mb-primary, #F54119)' }}><span><b>Acompte à payer maintenant ({pb.deposit_percent} %)</b></span><span><b>{FC(pb.deposit_amount)}</b></span></div>
                {pb.balance_amount > 0 && <div className="mk-total"><span>Solde ({quote.balance_mode === 'via_mbiyo' ? 'dans Mbiyo' : 'au propriétaire'})</span><span>{FC(pb.balance_amount)}</span></div>}
                {pb.security_deposit_amount > 0 && <div className="mk-total"><span>Caution (hors Mbiyo)</span><span>{FC(pb.security_deposit_amount)}</span></div>}
              </>
            ) : null}

          <div className="mk-pay">
            <button className={pay === 'balance' ? 'on' : ''} onClick={() => setPay('balance')}><Wallet size={18} />Solde Mbiyo</button>
            <button className={pay === 'mobile_money' ? 'on' : ''} onClick={() => setPay('mobile_money')}><Smartphone size={18} />Mobile Money</button>
          </div>
          {pay === 'mobile_money' && <div className="mk-field" style={{ marginTop: 8 }}><label>Numéro Mobile Money</label><input value={phone} onChange={e => setPhone(e.target.value)} placeholder="0991234567" /></div>}
          {phase && <p className="mk-note" style={{ marginTop: 8 }}>{phase}</p>}
          {error && <p className="mk-note err" style={{ marginTop: 8 }}>{error}</p>}
          <button className="mk-btn primary block" style={{ marginTop: 12 }} disabled={busy || !quote?.available || (pay === 'mobile_money' && !phone)} onClick={book}>
            {busy ? 'Réservation…' : pb ? `Payer l'acompte (${FC(pb.deposit_amount)}) et réserver` : 'Réserver'}
          </button>
        </div>
      </div>
    </div>
  );
}

const VSTATUS = {
  confirmed: ['Confirmée', '#10B981'], in_progress: ['En cours', '#FF7800'], completed: ['Terminée', '#5C636E'], no_show: ['Absence', '#DC2626'],
  cancelled_by_client: ['Annulée', '#DC2626'], cancelled_by_supplier: ['Annulée par le propriétaire', '#DC2626'], cancelled_by_admin: ['Annulée par Mbiyo', '#DC2626'],
};

export function VenueBookings({ nav, currentUser }) {
  const [bookings, setBookings] = useState(null);
  const [phone, setPhone] = useState('');
  const [phase, setPhase] = useState('');
  const load = () => apiService.get('/venue-bookings/mine').then(r => setBookings(r?.bookings || [])).catch(() => setBookings([]));
  useEffect(() => { load(); }, []);

  const cancel = async (b) => {
    const r0 = await apiService.get(`/venue-bookings/${b.booking_id}`).catch(() => null);
    const prev = r0?.cancellation_preview;
    if (!window.confirm(`Annuler cette réservation ?${prev ? `\nRemboursement de l'acompte : ${prev.refund_percent} % (${FC(prev.refund_amount)}) sur votre solde Mbiyo.` : ''}`)) return;
    const r = await apiService.patch(`/venue-bookings/${b.booking_id}/cancel`, {}).catch(e => e);
    if (!r?.success) alert(r?.error || r?.message || 'Annulation impossible');
    load();
  };
  const payBalance = async (b, method) => {
    try {
      let serdipay_payment_id = null;
      if (method === 'mobile_money') {
        if (!phone) { alert('Indiquez votre numéro Mobile Money dans le champ en haut.'); return; }
        const r = await mmPay({ userId: currentUser?.user_id, amount: b.price_breakdown.balance_amount, phone, label: `Solde ${b.venue?.name || 'salle'}`, planType: 'venue_balance', onPhase: setPhase });
        serdipay_payment_id = r.paymentId;
      }
      const res = await apiService.post(`/venue-bookings/${b.booking_id}/pay-balance`, { payment_method: method, ...(serdipay_payment_id ? { serdipay_payment_id } : {}) });
      if (!res?.success) throw new Error(res?.error);
      load();
    } catch (e) { alert(e?.error || e?.message || 'Paiement impossible'); }
    finally { setPhase(''); }
  };

  if (bookings === null) return <div className="mk-center"><div className="mk-spin" /> Chargement…</div>;
  return (
    <div className="mk-container">
      <div className="mk-panel" style={{ marginTop: 16 }}>
        <h3>🎉 Mes réservations de salles</h3>
        {bookings.some(b => b.balance_mode === 'via_mbiyo' && b.balance_status === 'pending') && (
          <div className="mk-field"><label>Numéro Mobile Money (pour payer un solde)</label><input value={phone} onChange={e => setPhone(e.target.value)} placeholder="0991234567" /></div>
        )}
        {phase && <p className="mk-note">{phase}</p>}
        {!bookings.length && <div className="mk-center">Aucune réservation. <button className="mk-btn primary" onClick={() => nav({ page: 'venues' })}>Voir les salles</button></div>}
        {bookings.map(b => {
          const [lbl, color] = VSTATUS[b.status] || [b.status, '#5C636E'];
          const pb = b.price_breakdown || {};
          return (
            <div className="mk-line" key={b.booking_id} style={{ alignItems: 'flex-start' }}>
              <div className="info">
                <div className="name">{b.venue?.name || 'Salle'}</div>
                <div className="opt">{b.date} · {b.slot?.label} · {b.guests_count} invités</div>
                <div className="opt">Total {FC(pb.total_amount)} · acompte payé {FC(pb.deposit_amount)}{pb.balance_amount > 0 ? ` · solde ${FC(pb.balance_amount)} ${b.balance_status === 'pending' ? (b.balance_mode === 'via_mbiyo' ? 'à payer dans Mbiyo' : 'à régler au propriétaire') : b.balance_status === 'received_by_owner' ? 'reçu par le propriétaire' : b.balance_status === 'paid_via_mbiyo' ? 'payé' : ''}` : ''}</div>
                {pb.security_deposit_amount > 0 && <div className="opt">Caution à remettre : {FC(pb.security_deposit_amount)}</div>}
                {b.receipt_number && <div className="opt">Reçu : <b>{b.receipt_number}</b></div>}
                <InvoiceLink type="venue" id={b.booking_id} compact />
                {b.cancellation?.refund_amount > 0 && <div className="opt" style={{ color: '#10B981' }}>Remboursé : {FC(b.cancellation.refund_amount)}</div>}
                {['confirmed', 'in_progress'].includes(b.status) && b.balance_mode === 'via_mbiyo' && b.balance_status === 'pending' && (
                  <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                    <button className="mk-btn primary" onClick={() => payBalance(b, 'balance')}>Payer le solde (solde Mbiyo)</button>
                    <button className="mk-btn ghost" onClick={() => payBalance(b, 'mobile_money')}>Mobile Money</button>
                  </div>
                )}
              </div>
              <span className="mk-open" style={{ background: `${color}1c`, color }}>{lbl}</span>
              {b.status === 'confirmed' && <button className="mk-iconbtn" title="Annuler" onClick={() => cancel(b)} style={{ color: 'var(--mb-danger)' }}>✕</button>}
            </div>
          );
        })}
      </div>
    </div>
  );
}
